import {
  autoUpdate,
  computePosition,
  flip,
  offset as offsetMiddleware,
  shift,
  size,
  type Middleware,
  type Placement,
} from "@floating-ui/dom";
import { useEffect } from "react";

export interface UseAnchorPositionOptions {
  /** Preferred side; flip may pick the opposite. */
  placement?: Placement;
  offset?: number;
  matchWidth?: boolean;
  /** Pass the open state. */
  enabled: boolean;
}

/** Fixed strategy because top-layer elements resolve coordinates against the viewport. */
export function useAnchorPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  floatingRef: React.RefObject<HTMLElement | null>,
  {
    placement = "bottom-start",
    offset = 6,
    matchWidth = false,
    enabled,
  }: UseAnchorPositionOptions,
): void {
  useEffect(() => {
    if (!enabled) return;
    const anchor = anchorRef.current;
    const floating = floatingRef.current;
    if (!anchor || !floating) return;

    const middleware: Middleware[] = [
      offsetMiddleware(offset),
      flip(),
      shift({ padding: 8 }),
    ];
    if (matchWidth) {
      middleware.push(
        size({
          apply({ rects, elements }) {
            elements.floating.style.width = `${rects.reference.width}px`;
          },
        }),
      );
    }

    let cancelled = false;
    const stop = autoUpdate(anchor, floating, () => {
      void computePosition(anchor, floating, {
        placement,
        strategy: "fixed",
        middleware,
      }).then(({ x, y }) => {
        if (cancelled) return;
        floating.style.position = "fixed";
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
        floating.style.margin = "0";
      });
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [anchorRef, floatingRef, enabled, placement, offset, matchWidth]);
}
